import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import clsx from 'clsx';
import styles from './AddEmergencyAsnFormProlife.module.css';
import { Modal } from '@material-ui/core';
import { reasons, agencies } from '../../staticData';
import { API } from '../../apis/api';
import {
  API_RESOURCE_URLS,
  DATE_FORMAT,
  DISPLAY_MESSAGES,
  REGEX,
} from '../../constants';
import {
  convertToTitleCase,
  isPositiveInteger,
  isQuantityValid,
  isEvrNumberValid,
} from '../../utils/utils';
import './AddEmergencyAsnFormProlife.module.css';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import {
  CustomDatePicker,
  CustomSelect,
  EllipsedText,
  ValidatingTextField,
} from '../FormComponents';
import { useErrorHandler } from 'react-error-boundary';
import { buildErrorMessage } from '../../apis/calls';

const AddEmergencyAsnFormProlife = ({
  handleClose,
  lastAsnGeneratedDate,
  open,
  updatePlanRows,
  skipToLastPage,
  plant,
  plantName,
  line,
  lineName,
}) => {
  const {
    showInternalError,
    showLoading,
    showError,
    showSuccess,
  } = usePopupManager();

  const handleError = useErrorHandler();

  const YES = 'yes';
  const NO = 'no';

  const formContainerRef = useRef(null);

  const [vc, setVc] = useState('');
  const [market, setMarket] = useState('');
  const [category, setCategory] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [defence, setDefence] = useState('');
  const [additionalQuantity, setAdditionalQuantity] = useState('');
  const [planDate, setPlanDate] = useState('');
  const [reason, setReason] = useState('');
  const [agency, setAgency] = useState('');
  const [comment, setComment] = useState('');
  const [
    displayVcCombinationNotUniqueError,
    setDisplayVcCombinationNotUniqueError,
  ] = useState(false);
  const [highlightMandatoryFields, setHighlightMandatoryFields] = useState(
    false,
  );
  const [firstEmergencyAsnDate, setFirstEmergencyAsnDate] = useState(null);

  const [countries, setCountries] = useState({ loading: false, data: [] });
  const [vcs, setVcs] = useState({ loading: false, data: [] });

  const [lastAsn, setLastAsn] = useState(null);
  const [evrNumber, setEvrNumber] = useState('');
  const [EvrOptions, setEvrOptions] = useState([]);
  const [evrNumberBalance, setEvrNumberBalance] = useState(null);

  const formatOptionsForSelect = (...options) =>
    options.map(option => ({ value: option, label: option }));

  const marketOptions = [
    { value: 'E', label: 'Export' },
    { value: 'D', label: 'Domestic' },
  ];

  const categoryOptions = formatOptionsForSelect('MTO', 'MTS');
  const yesAndNoOptions = [
    { value: YES, label: 'Yes' },
    { value: NO, label: 'No' },
  ];

  const reasonOptions = formatOptionsForSelect(...reasons);
  const agencyOptions = formatOptionsForSelect(...agencies);

  const handleSelectionCurry = setState => valueObject => setState(valueObject);
  const isLucknowProlifePlant = plantName === 'Lucknow-Prolife'
  const parentName = isLucknowProlifePlant ? 'Part' : 'VC'

  const getEvrBalance = (evrNumber) => {
    const selectedOption = EvrOptions.find(option => option.value === evrNumber);
    return selectedOption ? selectedOption.balenceQuantity : 0;
  };
  
  const handleSelectionCurryEvr = setState => valueObject => {
    setState(valueObject);
    const balance = getEvrBalance(valueObject.value);
    setEvrNumberBalance(balance);
  };

  const loadData = async (setState, url, params) => {
    try {
      const response = await API.get(url, {
        params: params,
      });
      setState({ loading: false, data: response.data });
    } catch (error) {
      handleError(error);
    }
  };

  const loadVcs = async () => {
    return loadData(setVcs, API_RESOURCE_URLS.VC_DESCRIPTION, {
      line,
      plant,
    });
  };

  const loadCountries = async () => {
    return loadData(setCountries, API_RESOURCE_URLS.COUNTRIES);
  };

  const loadEvrNumber = async () => {
    try {
      const country = countryCode.label.split(" - ")[1];
      const response = await API.get(API_RESOURCE_URLS.getEvrNumberOptions(plant),{
        params: {
          vc:vc.value,
          plannedDate: planDate, 
          countryCode: countryCode.value,
          country: country,
          line:line,
        }
      }); 
      const formattedOptions = response.data.map(({ evrNumber, balenceQuantity }) => ({
        label: evrNumber,
        value: evrNumber, 
        balenceQuantity
      })); 
      setEvrOptions(formattedOptions);
    } catch (error) {
      console.error('Error fetching EVR options:', error);
    }
  };

  const loadLastAsnForLine = async (lastAsnDate, selectedLine) => {
    if (!lastAsnDate) return setLastAsn(null);
    const dateString = lastAsnDate.format(DATE_FORMAT.ISO);
    try {
      const response = await API.get(API_RESOURCE_URLS.VC_ASN, {
        params: {
          from: dateString,
          to: dateString,
          line: selectedLine,
          plant,
        },
      });
      const asns = response.data;
      const lastSerialNumber = Math.max(...asns.map(asn => asn.serialNumber));
      const asnWithLastSerialNumber = asns.find(
        asn => asn.serialNumber === lastSerialNumber,
      );
      const lastAsnString = asnWithLastSerialNumber
        ? asnWithLastSerialNumber.asn
        : null;
      setLastAsn(lastAsnString);
    } catch (error) {
      console.error(
        'failed to get last asn on add emergency asn, err: ',
        buildErrorMessage(error),
      );
      showInternalError();
    }
  };

  const loadFirstEmergencyAsnDate = async () => {
    try {
      const response = await API.get(
        API_RESOURCE_URLS.FIRST_EMERGENCY_ASN_DATE,
        {
          params: {
            plant,
            line,
          },
        },
      );
      const data = response.data;
      const lineData = data.filter(entry => entry.line === line)[0];
      setFirstEmergencyAsnDate(moment(lineData.lastDate, DATE_FORMAT.ISO));
    } catch (error) {
      console.warn(
        'failed to get fist-possible-emergency-asn-date' +
          buildErrorMessage(error),
      );
      setFirstEmergencyAsnDate(null);
    }
  };

  const resetState = async () => {
    setVc(null);
    setMarket(null);
    setCategory(null);
    setCountryCode(null);
    setDefence(null);
    setAdditionalQuantity(null);
    setPlanDate(null);
    setReason(null);
    setAgency(null);
    setComment(null);
    setHighlightMandatoryFields(null);
    setLastAsn(null);
    setEvrNumber(null);
    setEvrNumberBalance(null);
  };

  useEffect(() => {
    if (open) {
      loadEvrNumber();
      loadVcs();
      loadCountries();
      loadLastAsnForLine(lastAsnGeneratedDate, line);
      loadFirstEmergencyAsnDate();
    } else {
      resetState();
      setFirstEmergencyAsnDate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lastAsnGeneratedDate, plant, line]);

  const getVcOptions = () =>
    formatOptionsForSelect(...vcs.data.map(({ vc }) => vc));

  const getDescriptionForVc = vcSelection => {
    const selectedVc = vcSelection && vcSelection.value;
    const selectedVcData = vcs.data.filter(({ vc }) => vc === selectedVc)[0];
    return selectedVcData ? selectedVcData.description : <strong>--</strong>;
  };
  const getMajorModelForVc = vcSelection => {
    const selectedVc = vcSelection && vcSelection.value;
    const selectedVcData = vcs.data.filter(({ vc }) => vc === selectedVc)[0];
    return selectedVcData ? selectedVcData.majorModel : <strong>--</strong>;
  };

  const getCountryCodeOptions = () => {
    return countries.data
      .map(({ code, country }) => ({
        value: code,
        label: `${code} - ${country}`,
      }))
      .sort((a, b) => {
        if (a.label < b.label) {
          return -1;
        }
        if (a.label > b.label) {
          return 1;
        }
        return 0;
      });
  };


  const isEvr = () => {
    return  market?.value !== 'E';
  }

  const validateFields = fields => {
    const {
      fbvVendorCode,
      buildCardNumber,
      powerSteering,
      ...requiredFields
    } = fields;
    const hasMissingRequiredFields = Object.values(requiredFields).some(
      field => !field,
    );
    if (hasMissingRequiredFields) {
      return false;
    }
    const { additionalQuantity } = fields;
    if (!isPositiveInteger(additionalQuantity)) return false;
    if (!isQuantityValid(additionalQuantity)) return false;
    if (market?.value === 'E' && !evrNumber) return false;
    return true;
  };

  const getSelectedVcCombination = () => ({
    category: category && category.value,
    countryCode: countryCode && countryCode.value,
    defence: defence && defence.value,
    market: market && market.value,
    vc: vc && vc.value
  });

  const doesVcHaveCostReleaseForDate = async (vc, dateString) => {
    try {
      const response = await API.get(API_RESOURCE_URLS.VC_COST_RELEASE, {
        params: {
          plannedDate: dateString,
          material: vc,
          plant,
        },
      });
      return response.data.released.includes(vc);
    } catch (error) {
      console.error(buildErrorMessage(error));
      showInternalError();
      return false;
    }
  };

  const checkForMandatoryFieldsAndGetSelectedVc = () => {
    const vcCombination = getSelectedVcCombination();
    const areFieldsValid = validateFields({
      ...vcCombination,
      reason,
      agency,
      additionalQuantity,
      planDate,
    });

    if (!areFieldsValid) {
      setHighlightMandatoryFields(true);
      return null;
    }

    return vcCombination.vc;
  };

  const handleSubmit = async () => {
    const vcCombination = getSelectedVcCombination();
    try {
      await API.patch(API_RESOURCE_URLS.VC_QUANTITY_SEQUENCE_ASN, {
        plant,
        line,
        ...vcCombination,
        planDate: planDate,
        additionalQuantity: Number(additionalQuantity),
        reason: reason.value,
        agency: agency.value,
        comment,
        evrNumber:evrNumber ? evrNumber.value : null,   
      });
      handleClose();
      showSuccess({
        contextText: `${isLucknowProlifePlant ? DISPLAY_MESSAGES.ASN_ADD_SUCCESS_PART : DISPLAY_MESSAGES.ASN_ADD_SUCCESS} ${moment(
          planDate,
          DATE_FORMAT.ISO,
        ).format('DD-MMM-YY')}`,
        info: `${parentName} ${vcCombination.vc}`,
      });
      await updatePlanRows();
      skipToLastPage();
    } catch (error) {
      console.error(buildErrorMessage(error));
      showError({
        contextText: DISPLAY_MESSAGES.ASN_ADD_FAILURE,
        info: '',
        error: error,
      });
    }
  };

  useEffect(() => {
    if (vc && planDate && countryCode) {
      loadEvrNumber(vc,planDate, countryCode);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vc, planDate, countryCode]);
  

  return (
    <>
      <Modal open={open} onClose={handleClose} disableBackdropClick>
        <div className={styles.addAsn} ref={formContainerRef}>
          <div className={styles.addAsnTitle}>
            <span>Plan Revision: Add ASN(s)</span>
            <div className={styles.plantInfo}>
              Line <strong>{lineName}</strong>
            </div>
            <div className={styles.plantInfo}>
              Plant <strong>{plantName}</strong>
            </div>
          </div>
          <div className={styles.frm}>
            <div className={styles.formSec}>
              <p className={styles.note}>
                Note: These ASN(s) will be added at the end of the Lineup for a
                selected Plan date.
                <span className={styles.optionalFields}>
                  ** Optional Fields
                </span>
              </p>

              <span className={styles.rowTitleTop}>Basic Info</span>
              <div className={styles.col8}>
                <div className={styles.formRow}>
                  <label className={styles.label}>{parentName} Number</label>
                  <CustomSelect
                    name="vc"
                    isMandatory
                    markIfUnselected={highlightMandatoryFields}
                    showError={displayVcCombinationNotUniqueError}
                    options={getVcOptions(vcs.data)}
                    className={clsx(styles.select, styles.sel1)}
                    value={vc}
                    onChange={handleSelectionCurry(setVc)}
                    maxMenuHeight={300}
                  />
                  <span className={clsx(styles.errorText)}>
                    {displayVcCombinationNotUniqueError
                      ? isLucknowProlifePlant
                        ? DISPLAY_MESSAGES.DUPLICATE_PART_COMBO_WARNING
                        : DISPLAY_MESSAGES.DUPLICATE_VC_COMBO_WARNING
                      : ""
                    }
                  </span>
                </div>
              </div>
              <div className={styles.col9}>
                <div className={clsx(styles.formRow, styles.descriptionCol)}>
                  <label className={styles.label}>Description</label>
                  <EllipsedText
                    value={getDescriptionForVc(vc, vcs.data)}
                    className={styles.description}
                  />
                </div>
              </div>
             {plantName === 'Pantnagar' && ( 
              <div className={styles.col9}>
                <div className={clsx(styles.formRow, styles.descriptionCol)}>
                  <label className={styles.label}>Major Model</label>
                  <EllipsedText
                    value={getMajorModelForVc(vc, vcs.data)}
                    className={styles.description}
                  />
                </div>
              </div>)}
            </div>
            <div className={clsx(styles.formSec, styles.vcDetail)}>
              <div className={styles.col2}>
              <span className={styles.rowTitle}>{parentName} Details</span>

                <div className={styles.formRow}>
                  <div className={styles.col4}>
                    <label className={styles.label}>Market</label>
                    <CustomSelect
                      name="market"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={marketOptions}
                      className={styles.select}
                      value={market}
                      onChange={handleSelectionCurry(setMarket)}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.col4}>
                    <label className={styles.label}>Country</label>
                    <CustomSelect
                      name="country"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={getCountryCodeOptions()}
                      className={styles.select}
                      value={countryCode}
                      onChange={handleSelectionCurry(setCountryCode)}
                    />
                  </div>
                </div>
                <div className={clsx(styles.formRow, styles.vCode)}>
                  <div className={styles.col4}>
                    <label className={styles.label}>Category</label>
                    <CustomSelect
                      name="category"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={categoryOptions}
                      className={styles.select}
                      value={category}
                      onChange={handleSelectionCurry(setCategory)}
                    />
                  </div>
                </div>
                <div className={clsx(styles.formRow, styles.defence)}>
                  <div className={styles.col4}>
                    <label className={styles.label}>Defence</label>
                    <CustomSelect
                      name="defence"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={yesAndNoOptions}
                      className={styles.select}
                      value={defence}
                      onChange={handleSelectionCurry(setDefence)}
                    />
                  </div>
                </div>

              </div>
              <div className={styles.col3}>
                <span className={styles.rowTitle}>
                  Plan Details
                </span>
                <div className={styles.formRow}>
                  <label className={clsx(styles.label, styles.lb1)}>
                    Plan date
                  </label>
                  <CustomDatePicker
                    isMandatory
                    markIfUnselected={highlightMandatoryFields}
                    autoOk
                    inputVariant="outlined"
                    value={planDate}
                    onChange={date => {
                      setPlanDate(date.format(DATE_FORMAT.ISO));
                    }}
                    inputProps={{
                      className: clsx(styles.select, styles.dateInput),
                    }}
                    emptyLabel="dd/mm/yyyy"
                    minDate={
                      process.env.NODE_ENV === 'test'
                        ? moment(1)
                        : firstEmergencyAsnDate || moment()
                    }
                    maxDate={
                      process.env.NODE_ENV === 'test'
                        ? moment('3020', 'YYYY')
                        : lastAsnGeneratedDate || moment()
                    }
                    format="DD/MM/yyyy"
                    data-testid="date-picker-div"
                  />
                </div>
                <div className={styles.formRow}>
                  <label className={clsx(styles.label, styles.lb1)}>
                    Additional Qty.
                  </label>
                  <ValidatingTextField
                    isMandatory
                    validationFn={isPositiveInteger}
                    markIfEmpty={highlightMandatoryFields}
                    isQuantityValid={isQuantityValid(additionalQuantity)}
                    validationHelperText={
                      DISPLAY_MESSAGES.POSITIVE_NUMBER_VALIDATION_WARNING
                    }
                    quantityValidationText={DISPLAY_MESSAGES.INVALID_INPUT}
                    variant="outlined"
                    value={additionalQuantity}
                    onChange={e => setAdditionalQuantity(e.target.value)}
                    placeholder="(Enter value)"
                    className={styles.textField}
                    inputProps={{
                      className: styles.textInput,
                      'data-testid': 'additional-quantity-input',
                    }}
                    FormHelperTextProps={{
                      className: styles.helperText,
                    }}
                  />  
                </div>
                <div className={styles.formRow}>
                  <label className={clsx(styles.label, styles.lb1)}>
                    EVR/DVR No.
                  </label>
                  <CustomSelect
                      name="EVR/DVR"
                      isMandatory
                      isDisabled={isEvr()}
                      markIfUnselected={highlightMandatoryFields}
                      options={EvrOptions}
                      className={styles.select}
                      value={evrNumber}
                      onChange={handleSelectionCurryEvr(setEvrNumber)}
                      data-testid='evr-dvr-dropdown'
                    />
                </div>
                <div className={styles.formRow}>
                  <label className={clsx(styles.label, styles.lb1)}>
                    EVR Balance
                  </label>
                  <ValidatingTextField
                      disabled
                      variant="outlined"
                      value={evrNumberBalance}
                      isQuantityValid
                      className={styles.newText}
                      inputProps={{
                        className: styles.newTextInput,
                        'data-testid': 'evr-balance-input',
                      }}
                      FormHelperTextProps={{
                        className: styles.helperText,
                      }}
                    />
                </div>
              </div>
              <div className={styles.col3}>
                <span className={clsx(styles.rowTitle)}>Last ASN</span>
                <div className={styles.formRowasn}>
                  <label className={clsx(styles.label, styles.lb1)}>
                    Planned for
                  </label>
                  <span className={styles.asnTxt}>
                    {lastAsnGeneratedDate
                      ? lastAsnGeneratedDate.format('DD-MM-YYYY')
                      : '--'}
                  </span>
                </div>
                <div className={styles.formRowasn}>
                  <label className={clsx(styles.label, styles.lb1)}>ASN</label>
                  <span className={styles.asnTxt}>{lastAsn || '--'}</span>
                </div>
              </div>
            </div>
            <div className={clsx(styles.formSec, styles.vcDetail)}>
              <span className={styles.rowTitle}>Revision Details </span>
              <div className={styles.col6}>
                <div className={styles.formRow}>
                  <label className={styles.label}>Reason</label>
                  <CustomSelect
                    name="reason"
                    isMandatory
                    markIfUnselected={highlightMandatoryFields}
                    options={reasonOptions}
                    className={clsx(styles.select, styles.sel1)}
                    value={reason}
                    onChange={handleSelectionCurry(setReason)}
                  />
                </div>
                <div className={styles.formRow}>
                  <label className={styles.label}>Agency</label>
                  <CustomSelect
                    name="agency"
                    isMandatory
                    markIfUnselected={highlightMandatoryFields}
                    options={agencyOptions}
                    className={clsx(styles.select, styles.sel1)}
                    value={agency}
                    onChange={handleSelectionCurry(setAgency)}
                  />
                </div>
              </div>
              <div className={styles.col7}>
                <div className={styles.formRow}>
                  <label className={clsx(styles.label, styles.commentsLabel)}>
                    Comments<br></br> (Optional)
                  </label>
                  <textarea
                    className={styles.comments}
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                  ></textarea>
                </div>
              </div>
            </div>
            <div className={styles.buttons}>
              <button onClick={handleClose} className={styles.cancel}>
                Cancel
              </button>
              <button
                className={styles.submit}
                onClick={async e => {
                  if (e.detail > 1) {
                    console.log('A double-click has been registered');
                    return;
                  }
                  const vc = checkForMandatoryFieldsAndGetSelectedVc();
                  if (vc) {
                    showLoading();
                    return doesVcHaveCostReleaseForDate(vc, planDate).then(
                      hasCostRelease => {
                        if (hasCostRelease) {
                          handleSubmit();
                        } else
                          showError({
                            contextText:
                              DISPLAY_MESSAGES.VC_COST_RELEASE_NOT_FOUND_FOR_SEQUENCE,
                            info: vc,
                          });
                        return false;
                      },
                    );
                  }
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

AddEmergencyAsnFormProlife.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  lastAsnGeneratedDate: PropTypes.objectOf(moment),
  updatePlanRows: PropTypes.func.isRequired,
  skipToLastPage: PropTypes.func.isRequired,
  plant: PropTypes.string.isRequired,
  plantName: PropTypes.string.isRequired,
  line: PropTypes.string.isRequired,
  lineName: PropTypes.string.isRequired,
};

export default AddEmergencyAsnFormProlife;
